import React, { useState, useEffect, useMemo } from 'react';

/**
 * AnimatedClock — Real-time ticking analog clock with 3D perspective,
 * glowing accents, orbiting rings, and optional digital readout.
 *
 * Props:
 *  - size        : number  (SVG viewBox dimension, default 280)
 *  - className   : string
 *  - showDigital : boolean (show digital time beneath clock)
 *  - compact     : boolean (smaller render, no orbiting rings)
 *  - glowColor   : string  (CSS color for accent, default '#6366f1')
 */
const AnimatedClock = ({
  size = 280,
  className = '',
  showDigital = true,
  compact = false,
  glowColor = '#6366f1',
  burst = false,
  lightDial = false,
}) => {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const id = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const s = time.getSeconds();
  const m = time.getMinutes();
  const h = time.getHours() % 12;

  const sDeg = s * 6;
  const mDeg = m * 6 + s * 0.1;
  const hDeg = h * 30 + m * 0.5;

  const cx = size / 2;
  const cy = size / 2;
  const outerR = size / 2 - 4;

  /* ---- helpers ---- */
  const pt = (deg, len) => ({
    x: cx + len * Math.cos((deg - 90) * Math.PI / 180),
    y: cy + len * Math.sin((deg - 90) * Math.PI / 180),
  });

  const hourHand   = pt(hDeg, outerR * 0.45);
  const minuteHand = pt(mDeg, outerR * 0.65);
  const secondHand = pt(sDeg, outerR * 0.78);
  const secondTail = pt(sDeg + 180, outerR * 0.18);

  /* ---- markers ---- */
  const hours = useMemo(() =>
    Array.from({ length: 12 }, (_, i) => {
      const num   = i + 1;
      const angle = (num * 30 - 90) * (Math.PI / 180);
      const major = num % 3 === 0;
      return {
        num,
        major,
        mx1: cx + (outerR - 5)  * Math.cos(angle),
        my1: cy + (outerR - 5)  * Math.sin(angle),
        mx2: cx + (outerR - (major ? 22 : 14)) * Math.cos(angle),
        my2: cy + (outerR - (major ? 22 : 14)) * Math.sin(angle),
        nx:  cx + (outerR - 34) * Math.cos(angle),
        ny:  cy + (outerR - 34) * Math.sin(angle),
      };
    }),
    [cx, cy, outerR],
  );

  const minuteMarkers = useMemo(() =>
    Array.from({ length: 60 }, (_, i) => {
      if (i % 5 === 0) return null;
      const angle = (i * 6 - 90) * (Math.PI / 180);
      return {
        x1: cx + (outerR - 5)  * Math.cos(angle),
        y1: cy + (outerR - 5)  * Math.sin(angle),
        x2: cx + (outerR - 11) * Math.cos(angle),
        y2: cy + (outerR - 11) * Math.sin(angle),
      };
    }).filter(Boolean),
    [cx, cy, outerR],
  );

  /* ---- digital display ---- */
  const pad = (n) => String(n).padStart(2, '0');
  const digital = `${pad(time.getHours())}:${pad(m)}:${pad(s)}`;

  /* ---- render size ---- */
  const renderSize = compact ? size * 0.7 : size;

  return (
    <div className={`relative inline-flex flex-col items-center ${className}`}>
      {/* Burst keyframes */}
      {burst && (
        <style>{`
          @keyframes acBurstHour {
            0%, 12% { transform: translate(0, 0) rotate(0deg); opacity: 1; }
            28%, 48% { transform: translate(-22px, 55px) rotate(-30deg); opacity: 0.1; }
            68%, 100% { transform: translate(0, 0) rotate(0deg); opacity: 1; }
          }
          @keyframes acBurstMinute {
            0%, 15% { transform: translate(0, 0) rotate(0deg); opacity: 1; }
            32%, 48% { transform: translate(18px, 65px) rotate(40deg); opacity: 0.1; }
            72%, 100% { transform: translate(0, 0) rotate(0deg); opacity: 1; }
          }
          @keyframes acBurstSecond {
            0%, 10% { transform: translate(0, 0) rotate(0deg); opacity: 1; }
            26%, 48% { transform: translate(4px, 78px) rotate(-50deg); opacity: 0.1; }
            65%, 100% { transform: translate(0, 0) rotate(0deg); opacity: 1; }
          }
        `}</style>
      )}

      {/* Ambient glow */}
      <div
        className="absolute rounded-full pointer-events-none"
        style={{
          width: renderSize + 60,
          height: renderSize + 60,
          top: -30,
          left: '50%',
          marginLeft: -(renderSize + 60) / 2,
          background: `radial-gradient(circle, ${glowColor}22 0%, transparent 70%)`,
          animation: 'glowPulse 3s ease-in-out infinite',
        }}
      />

      {/* Orbiting rings (desktop hero only) */}
      {!compact && (
        <>
          <div
            className="absolute rounded-full border border-dashed pointer-events-none"
            style={{
              width: renderSize + 44,
              height: renderSize + 44,
              top: -22,
              left: '50%',
              marginLeft: -(renderSize + 44) / 2,
              borderColor: `${glowColor}30`,
              animation: 'spin 25s linear infinite',
            }}
          />
          <div
            className="absolute rounded-full border border-dashed pointer-events-none"
            style={{
              width: renderSize + 76,
              height: renderSize + 76,
              top: -38,
              left: '50%',
              marginLeft: -(renderSize + 76) / 2,
              borderColor: `${glowColor}18`,
              animation: 'spin 40s linear infinite reverse',
            }}
          />
          {/* Small orbiting dot */}
          <div
            className="absolute rounded-full pointer-events-none"
            style={{
              width: 6,
              height: 6,
              background: glowColor,
              boxShadow: `0 0 10px ${glowColor}`,
              top: -22,
              left: '50%',
              marginLeft: (renderSize + 44) / 2 - 3,
              transformOrigin: `3px ${(renderSize + 44) / 2 + 22}px`,
              animation: 'spin 25s linear infinite',
            }}
          />
        </>
      )}

      {/* 3D perspective wrapper */}
      <div style={{ perspective: '1000px' }}>
        <div
          className="animate-float"
          style={{
            transformStyle: 'preserve-3d',
            transform: compact ? 'rotateX(6deg)' : 'rotateX(10deg) rotateY(-5deg)',
          }}
        >
          <svg
            width={renderSize}
            height={renderSize}
            viewBox={`0 0 ${size} ${size}`}
            aria-label="Animated analog clock"
          >
            <defs>
              <filter id="acGlow">
                <feGaussianBlur stdDeviation="2" result="b" />
                <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
              <filter id="acSecGlow">
                <feGaussianBlur stdDeviation="3" result="b" />
                <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
              <linearGradient id="acFace" x1="0%" y1="0%" x2="100%" y2="100%">
                {lightDial ? (
                  <>
                    <stop offset="0%" stopColor="#f59e0b" />
                    <stop offset="45%" stopColor="#d97706" />
                    <stop offset="100%" stopColor="#fbbf24" />
                  </>
                ) : (
                  <>
                    <stop offset="0%" stopColor="rgba(15,15,40,0.9)" />
                    <stop offset="100%" stopColor="rgba(5,5,20,0.95)" />
                  </>
                )}
              </linearGradient>
              <linearGradient id="acRim" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={`${glowColor}55`} />
                <stop offset="50%" stopColor={`${glowColor}18`} />
                <stop offset="100%" stopColor={`${glowColor}44`} />
              </linearGradient>
            </defs>

            {/* Face */}
            <circle cx={cx} cy={cy} r={outerR} fill="url(#acFace)" stroke="url(#acRim)" strokeWidth="2.5" />
            <circle cx={cx} cy={cy} r={outerR - 7} fill="none" stroke={lightDial ? 'rgba(255,255,255,0.2)' : `${glowColor}15`} strokeWidth="0.5" />
            {/* Inner decorative ring */}
            <circle cx={cx} cy={cy} r={outerR * 0.32} fill="none" stroke={lightDial ? 'rgba(255,255,255,0.15)' : `${glowColor}0a`} strokeWidth="0.5" strokeDasharray="3 6" />

            {/* Minute markers */}
            {minuteMarkers.map((mk, i) => (
              <line key={i} x1={mk.x1} y1={mk.y1} x2={mk.x2} y2={mk.y2}
                stroke={lightDial ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.12)'} strokeWidth="1" strokeLinecap="round" />
            ))}

            {/* Hour markers + numbers */}
            {hours.map((hr) => (
              <g key={hr.num}>
                <line
                  x1={hr.mx1} y1={hr.my1} x2={hr.mx2} y2={hr.my2}
                  stroke={hr.major
                    ? (lightDial ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.6)')
                    : (lightDial ? 'rgba(255,255,255,0.55)' : 'rgba(255,255,255,0.3)')}
                  strokeWidth={hr.major ? '2.5' : '1.5'}
                  strokeLinecap="round"
                />
                {hr.major && (
                  <text
                    x={hr.nx} y={hr.ny}
                    textAnchor="middle" dominantBaseline="central"
                    fill={lightDial ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.4)'}
                    fontSize={size * 0.055}
                    fontFamily="'Inter', system-ui, sans-serif"
                    fontWeight={lightDial ? '700' : '600'}
                  >
                    {hr.num}
                  </text>
                )}
              </g>
            ))}

            {/* OPERIS label */}
            <text
              x={cx} y={cy + outerR * 0.28}
              textAnchor="middle" dominantBaseline="central"
              fill={lightDial ? 'rgba(255,255,255,0.45)' : `${glowColor}50`}
              fontSize={size * 0.032}
              fontFamily="'Inter', system-ui, sans-serif"
              fontWeight="700"
              letterSpacing="0.25em"
            >
              OPERIS
            </text>

            {/* ── Hands ── */}
            {/* Hour */}
            <g style={burst ? { transformOrigin: `${cx}px ${cy}px`, animation: 'acBurstHour 8s ease-in-out 1s infinite' } : undefined}>
              <line x1={cx} y1={cy} x2={hourHand.x} y2={hourHand.y}
                stroke="white" strokeWidth="4" strokeLinecap="round" filter="url(#acGlow)" />
            </g>
            {/* Minute */}
            <g style={burst ? { transformOrigin: `${cx}px ${cy}px`, animation: 'acBurstMinute 8s ease-in-out 1s infinite' } : undefined}>
              <line x1={cx} y1={cy} x2={minuteHand.x} y2={minuteHand.y}
                stroke="rgba(255,255,255,0.85)" strokeWidth="2.5" strokeLinecap="round" filter="url(#acGlow)" />
            </g>
            {/* Second */}
            <g style={burst ? { transformOrigin: `${cx}px ${cy}px`, animation: 'acBurstSecond 8s ease-in-out 1s infinite' } : undefined}>
              <line x1={secondTail.x} y1={secondTail.y} x2={secondHand.x} y2={secondHand.y}
                stroke={glowColor} strokeWidth="1.5" strokeLinecap="round" filter="url(#acSecGlow)" />
              <circle cx={secondHand.x} cy={secondHand.y} r="2.5" fill={glowColor} filter="url(#acSecGlow)" />
            </g>

            {/* Center */}
            <circle cx={cx} cy={cy} r="5" fill={glowColor} filter="url(#acSecGlow)" />
            <circle cx={cx} cy={cy} r="2" fill="white" />
          </svg>
        </div>
      </div>

      {/* Digital readout */}
      {showDigital && (
        <div className="mt-5 rounded-xl border border-white/10 dark:border-white/10 bg-white/[0.04] dark:bg-white/[0.04] px-5 py-2 backdrop-blur-sm" style={{ borderColor: glowColor + '25', backgroundColor: glowColor + '08' }}>
          <span className="font-mono text-xl tracking-[0.25em] tabular-nums md:text-2xl" style={{ color: glowColor + 'bb' }}>
            {digital.split('').map((ch, i) => (
              <span
                key={i}
                className={
                  ch === ':'
                    ? 'animate-blink mx-px'
                    : i >= 6
                      ? ''
                      : ''
                }
                style={ch === ':' ? { color: glowColor } : i >= 6 ? { color: glowColor } : undefined}
              >
                {ch}
              </span>
            ))}
          </span>
        </div>
      )}
    </div>
  );
};

export default AnimatedClock;
